// src/pages/Auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import AuthService from '../../services/authService';
import { BRAND_CONFIG, getLogoSrc } from '../../config/brandConfig';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ThemeToggle, LanguageToggle } from '../../components/common/UiControls';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useAppTheme();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);

  const successMessage = location.state?.message;
  const logoSrc = getLogoSrc(darkMode);

  useEffect(() => {
    localStorage.setItem('rememberMe', String(rememberMe));
  }, [rememberMe]);

  useEffect(() => {
    let interval;
    if (isBlocked && blockTimer > 0) {
      interval = setInterval(() => {
        setBlockTimer((prev) => {
          if (prev <= 1) {
            setIsBlocked(false);
            setLoginAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isBlocked, blockTimer]);

  const validate = () => {
    const nextErrors = {};
    if (!formData.email) {
      nextErrors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = t('auth.emailInvalid');
    }
    if (!formData.password) {
      nextErrors.password = t('auth.passwordRequired');
    } else if (formData.password.length < 6) {
      nextErrors.password = t('auth.passwordMinLength');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isBlocked) {
      setLoginError(t('auth.tooManyAttemptsWait', { seconds: blockTimer }));
      return;
    }
    if (!validate()) return;

    setLoading(true);
    setLoginError('');

    try {
      const result = await AuthService.login({ ...formData, rememberMe });

      if (result.success) {
        AuthService.setAuthData(
          result.data.token,
          result.data.user,
          rememberMe,
          result.data.refreshToken
        );
        setLoginAttempts(0);
        window.dispatchEvent(new CustomEvent('userLogin'));
        navigate('/edefter/dashboard', { replace: true });
      } else {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);

        if (newAttempts >= 3) {
          setIsBlocked(true);
          setBlockTimer(300);
          setLoginError(t('auth.blocked5min'));
        } else {
          setLoginError(`${result.message} ${t('auth.failedAttempts', { count: newAttempts, remaining: 3 - newAttempts })}`);
        }
      }
    } catch (error) {
      setLoginError(t('auth.serverError'));
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setLoginError('');
  };

  const inputClass = (hasError) =>
    `w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-[#2C2C2E] border ${
      hasError ? 'border-red-500' : 'border-transparent'
    } focus:outline-none focus:ring-2 focus:ring-[#24299d] transition-all text-gray-900 dark:text-white`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="flex justify-end items-center gap-2 mb-4">
          <LanguageToggle />
          <ThemeToggle />
        </div>

        <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl shadow-xl p-8 border border-gray-100 dark:border-white/10">
          <div className="text-center mb-8">
            <img
              src={logoSrc}
              alt={BRAND_CONFIG.name}
              className="w-3/4 h-16 object-contain mx-auto mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.loginTitle')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{BRAND_CONFIG.tagline}</p>
          </div>

          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <p className="text-sm text-green-600 dark:text-green-400">{successMessage}</p>
            </div>
          )}

          {loginError && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-sm text-red-600 dark:text-red-400">
                {isBlocked ? (
                  <>
                    <span className="font-semibold block">{t('auth.securityLockActive')}</span>
                    {t('auth.timeRemaining')} {formatTime(blockTimer)}
                  </>
                ) : (
                  loginError
                )}
              </p>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isBlocked}
                  className={inputClass(!!errors.email)}
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isBlocked}
                  className={`${inputClass(!!errors.password)} pr-12`}
                  placeholder={t('auth.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isBlocked}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isBlocked}
                  className="w-4 h-4 rounded accent-[#24299d]"
                />
                {t('auth.rememberMe')}
              </label>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                disabled={isBlocked}
                className="text-sm font-semibold text-[#24299d] hover:text-[#1b2080] dark:text-[#818cf8]"
              >
                {t('auth.forgotPasswordQuestion')}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || isBlocked}
              className="w-full h-12 rounded-xl bg-[#24299d] hover:bg-[#1b2080] text-white font-semibold shadow-lg shadow-[#24299d]/20 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading
                ? t('auth.loggingIn')
                : isBlocked
                  ? t('auth.blockedTimer', { time: formatTime(blockTimer) })
                  : t('auth.loginButton')}
            </button>

            {loginAttempts > 0 && !isBlocked && (
              <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                {t('auth.failedAttempts', { count: loginAttempts, remaining: 3 - loginAttempts })}
              </p>
            )}
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-white/10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('auth.noAccount')}{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                disabled={isBlocked}
                className="font-semibold text-[#24299d] hover:text-[#1b2080] dark:text-[#818cf8]"
              >
                {t('auth.createAccount')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
